/* eslint-disable spire/export-styles */
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductList/Handlers/ChangeUnitOfMeasure";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { CustomIncludeValues } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { ApplicationClipboardState } from "@thkStore/Store/Widgets/Clipboard/ClipboardReducer";
import changeClipboardItem from "@thkStore/Store/Widgets/Clipboard/Handlers/ChangeClipboardItem";
import removeClipboardItem from "@thkStore/Store/Widgets/Clipboard/Handlers/RemoveClipboardItem";
import React, { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    product: ProductModel;
    idx?: number;
    pricing?: ProductPriceDto;
    inventory?: ProductInventoryDto;
    parentProductId: string;
}

const mapDispatchToProps = {
    changeUnitOfMeasure,
    changeClipboardItem,
    removeClipboardItem
};

type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface VariantChildrenGridContextModel extends ProductContextModel {
    addIncludes: boolean;
    qtyOrdered: number;
    parentProductId: string;
    onAddIncludesChanged?: (addIncludes: boolean) => void;
    onCustomRailConfirmed?: (values: CustomIncludeValues) => void;
}

const VariantChildrenGridContext: FC<PropsWithChildren<Props>> = ({
    product,
    children,
    changeUnitOfMeasure,
    changeClipboardItem,
    removeClipboardItem,
    idx,
    pricing,
    inventory,
    parentProductId,
}) => {


    const [qtyOrdered, setQtyOrdered] = React.useState(0);
    const [addIncludes, setAddIncludes] = React.useState(false);

    const ownClipboard = useSelector(
        (state: ApplicationClipboardState) => state.clipboard.items[product.id]
    ); // subscribe to clipboard changes to update context when clipboard is updated from other places in the app

    useEffect(() => {
        if (ownClipboard) {
            setQtyOrdered(ownClipboard.qtyOrdered);
            setAddIncludes(ownClipboard.addIncludes);
        }
        else {
            setQtyOrdered(0);
            setAddIncludes(false);
        }
    }, [ownClipboard]);

    const productInfo = useMemo(() => {
        const info = createFromProduct(product);
        info.qtyOrdered = qtyOrdered || 0;
        if (pricing) {
            info.pricing = pricing;
        }
        if (inventory) {
            info.inventory = inventory;
        }
        return info;
    }, [product, qtyOrdered, pricing, inventory]);

    // Memoized so the Provider value reference is stable across re-renders; otherwise every
    // parent re-render would push a new context value and force-render all consumer rows.
    const productContext: VariantChildrenGridContextModel = useMemo(() => {
        const onQtyOrderedChanged = (newQtyOrdered: number) => {
            setQtyOrdered(newQtyOrdered);
            if (newQtyOrdered > 0) {
                changeClipboardItem({
                    productId: product.id,
                    qtyOrdered: newQtyOrdered,
                    productName: product.productTitle,
                    addIncludes,
                    viewOnly: false,
                });
                return;
            }
            removeClipboardItem({ productId: product.id });
        };

        const onAddIncludesChanged = (newAddIncludes: boolean) => {
            setAddIncludes(newAddIncludes);
            changeClipboardItem({
                productId: product.id,
                qtyOrdered,
                productName: product.productTitle,
                addIncludes: newAddIncludes,
                viewOnly: false,
            });
        };

        const onCustomRailConfirmed = (values: CustomIncludeValues) => {
            setQtyOrdered(1);
            changeClipboardItem({
                productId: product.id,
                productName: values.partNo || product.productTitle,
                qtyOrdered: 1,
                addIncludes,
                viewOnly: false,
                partNo: values.partNo,
                unitPrice: values.unitPrice,
                weight: values.weight,
                length: values.length,
                g1: values.g1,
                g2: values.g2,
                daysToShip: values.daysToShip,
            });
        };

        return {
            product,
            productInfo,
            idx,
            addIncludes,
            qtyOrdered,
            parentProductId,
            onQtyOrderedChanged,
            onUnitOfMeasureChanged: unitOfMeasure => changeUnitOfMeasure({ productId: product.id, unitOfMeasure }),
            onAddIncludesChanged,
            onCustomRailConfirmed,
        };
    }, [
        product,
        productInfo,
        idx,
        addIncludes,
        qtyOrdered,
        parentProductId,
        changeClipboardItem,
        removeClipboardItem,
        changeUnitOfMeasure,
    ]);

    return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
};

export default connect(null, mapDispatchToProps)(VariantChildrenGridContext);